import { Waves, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { MONTHS } from "@/lib/format";

const SOURCE_LABEL = {
  history: "from prior-year actuals uploaded in Step 1",
  ly: "from the Last Year columns of the P&L files",
  "ly+bali": "Last Year columns where available, Bali default curve for the rest",
  bali: "Bali hotel default curve (no prior-year data yet)",
  manual: "manual",
  none: "flat",
};
const MODES = [{ id: "auto", label: "Auto" }, { id: "manual", label: "Manual" }, { id: "none", label: "None (flat)" }];

export const SeasonalityPanel = ({ a, set, season }) => {
  const mode = a.seasonal_mode || "auto";
  const auto = season?.auto || season?.index || Array(12).fill(100);
  const manual = a.seasonal_index || [];
  const shown = mode === "none" ? Array(12).fill(100) : mode === "manual" ? MONTHS.map((_, i) => (manual[i] ?? auto[i])) : auto;
  const max = Math.max(...shown.map((v) => Number(v) || 0), 1);
  const setMode = (m) => set("seasonal_mode", m);
  const setIdx = (i, v) => set("seasonal_index", MONTHS.map((_, j) => (j === i ? (v === "" ? null : Number(v)) : (manual[j] ?? auto[j]))));
  const avg = shown.reduce((s, v) => s + (Number(v) || 0), 0) / 12;
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6" data-testid="seasonality-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900 inline-flex items-center gap-2"><Waves size={16} className="text-emerald-700" /> Seasonality</h3>
          <p className="text-xs text-slate-500 mt-1">Monthly index (100 = average month). Shapes how YTD totals are spread over months without a file and the "× seasonal index" forecast method.</p>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50 text-xs" data-testid="seasonal-mode-toggle">
          {MODES.map((m) => <button key={m.id} type="button" onClick={() => setMode(m.id)} data-testid={`seasonal-mode-${m.id}`} className={`px-3 py-1.5 rounded ${mode === m.id ? "bg-slate-900 text-white" : "text-slate-600"}`}>{m.label}</button>)}
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-3" data-testid="seasonal-source">
        {mode === "auto" && <>Source: <span className="font-medium text-slate-700">{SOURCE_LABEL[season?.source] || "automatic"}</span>. Upload prior-year P&amp;L files in Step 1 for a property-specific curve.</>}
        {mode === "manual" && <>Enter an index per month; blank = automatic value ({SOURCE_LABEL[season?.auto_source || season?.source] || "auto"}). Values are normalised so the average is 100.</>}
        {mode === "none" && <>All months treated equally — forecast months stay flat and YTD is spread evenly.</>}
      </p>
      <div className="mt-4 grid grid-cols-6 sm:grid-cols-12 gap-1.5" data-testid="seasonal-index-grid">
        {MONTHS.map((m, i) => {
          const v = Number(shown[i]) || 0;
          return (
            <div key={m} className="flex flex-col items-center gap-1" data-testid={`seasonal-month-${i + 1}`}>
              <div className="h-16 w-full flex items-end rounded bg-slate-50 border border-slate-100 px-1">
                <div className={`w-full rounded-t ${v >= avg ? "bg-emerald-500" : "bg-slate-300"}`} style={{ height: `${Math.max(4, (v / max) * 100)}%` }} />
              </div>
              <span className="text-[10px] text-slate-500">{m}</span>
              {mode === "manual" ? (
                <Input type="number" step="1" min="1" value={manual[i] ?? ""} placeholder={String(Math.round(auto[i]))} onChange={(e) => setIdx(i, e.target.value)} className="font-mono h-7 px-1 text-[11px] text-center" data-testid={`seasonal-index-input-${i + 1}`} />
              ) : (
                <span className="font-mono text-[11px] text-slate-700" data-testid={`seasonal-index-value-${i + 1}`}>{Math.round(v)}</span>
              )}
            </div>
          );
        })}
      </div>
      {mode === "manual" && (
        <button type="button" onClick={() => set("seasonal_index", null)} className="mt-3 inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold hover:underline" data-testid="seasonal-reset-auto"><RotateCcw size={11} /> Reset to automatic values</button>
      )}
    </section>
  );
};
